/**
 * API Classification & Caching Configuration
 * Defines cache strategies for all API endpoints
 */

export const API_CATEGORIES = {
  STATIC: 'static',
  SEMI_DYNAMIC: 'semi_dynamic',
  REAL_TIME: 'real_time',
};

export const CACHE_CONFIG = {
  // ===== STATIC/IMMUTABLE APIs =====
  // Data that rarely changes or is immutable once created (Reports, Shared Content)
  [API_CATEGORIES.STATIC]: {
    cacheDuration: 30 * 24 * 60 * 60 * 1000, // 30 days
    strategy: 'cache-first',
    storage: 'indexeddb',
    revalidate: false,
    endpoints: [
      '/api/interview-types',
      '/api/companies',
      '/api/feature-flags/public',
      '/api/feature-flags/my',
      '/api/learn',
      '/api/learn/certificates',
      '/session/',               // Completed interview reports/details
      '/interview-report',       // Report page data
      '/api/sandbox/share/',     // Shared code snippets
      '/api/blogs/',             // Individual blog post content
      '/api/forum/posts/',       // Individual forum post details
      '/api/interviews/*/benchmark', // Specific interview benchmarks
      '/api/interviews?*',       // Specific interview details (ID)
      '/api/interviews/company-stats', // Historical company stats
    ],
  },

  // ===== SEMI-DYNAMIC APIs =====
  // Content that updates periodically but is good to show stale while fetching fresh
  [API_CATEGORIES.SEMI_DYNAMIC]: {
    cacheDuration: 5 * 60 * 1000, // 5 minutes
    strategy: 'stale-while-revalidate',
    storage: 'indexeddb',
    revalidate: true,
    staleTime: 1 * 60 * 1000, // 1 minute before considering stale
    updateInterval: 5 * 60 * 1000, // Background fetch every 5 minutes
    endpoints: [
      '/auth/me',               // Profile data
      '/auth/profile',          // Extended profile
      '/api/users/profile',      // Leaderboard profile view
      '/api/blogs',             // Blog list
      '/api/forum/posts',       // Forum list
      '/api/forum/stats',       // Community stats
      '/api/forum/trending',    // Trending topics
      '/api/forum/my-posts',    // User's own posts
      '/api/forum/posts/*/comments', // Post comments
      '/api/newsletter',        // Subscription status
      '/api/interviews',        // Interview history list
      '/api/interviews/stats',  // User performance stats
      '/api/institution-assignments', // Student's assigned institutional interviews
      '/api/activity/stats',    // Heatmap data
      '/api/achievements',      // User achievements
      '/api/my-plans',          // User's learning plans
      '/api/my-plans/*/schedule', // Plan schedules
      '/api/scheduled-interviews', // Upcoming sessions
      '/api/strengths',         // Computed strengths
      '/api/weak-areas',        // Computed weak areas
      '/api/payments',          // User payment history
      '/auth/notifications',    // User notification settings
      '/api/referrals/code',    // Referral program data
      '/api/rankings/',          // Leaderboard rankings
      '/api/friends',           // Friend list
      '/api/friends/suggestions', // Friend suggestions
      '/api/friends/activity',   // Friend activity feed
    ],
  },

  // ===== REAL-TIME APIs =====
  // Security, sessions, and heavy processing results — must always be fresh
  [API_CATEGORIES.REAL_TIME]: {
    cacheDuration: 30 * 1000, // 30 seconds (minimal caching)
    strategy: 'network-first',
    storage: 'memory',
    revalidate: true,
    endpoints: [
      '/auth/login',
      '/auth/register',
      '/auth/google',
      '/auth/verify-email',
      '/auth/resend-verification',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/create-session',
      '/end-session/',
      '/api/system/status',     // Maintenance/System health
      '/api/interviews/execute', // Code execution
      '/api/resume/analyze',    // Resume parsing
      '/api/resume/generate-optimized',
      '/api/interviews/*/feedback',
      '/api/forum/vote',
      '/api/friends/request',   // Friend requests (POST)
      '/api/friends/respond',   // Friend response (POST)
    ],
  },
};

// Pricing is controlled by the admin and must always reflect the current
// catalog. Keep this endpoint out of memory, IndexedDB, and offline fallbacks.
const NO_CACHE_ENDPOINTS = new Set(['/api/plans']);

/**
 * Helper to normalize URL to path only
 */
export const normalizeUrl = (url) => {
  if (!url) return '';
  try {
    if (url.startsWith('http')) {
      return new URL(url).pathname;
    }
    // Remove query string if present for path comparison
    return url.split('?')[0];
  } catch (e) {
    return url.split('?')[0];
  }
};

/**
 * Helper to get config for a specific endpoint
 */
export const getCacheConfig = (url) => {
  const path = normalizeUrl(url);

  if (NO_CACHE_ENDPOINTS.has(path)) {
    return {
      category: API_CATEGORIES.REAL_TIME,
      cacheDuration: 0,
      strategy: 'network-first',
      storage: 'memory',
      revalidate: false,
      endpoints: [],
    };
  }

  // Define categories in priority order
  const categories = [
    API_CATEGORIES.STATIC,
    API_CATEGORIES.SEMI_DYNAMIC,
    API_CATEGORIES.REAL_TIME
  ];

  for (const category of categories) {
    const config = CACHE_CONFIG[category];
    if (config.endpoints.some(pattern => {
      // 1. Handle wildcard patterns
      if (pattern.includes('*')) {
        const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp('^' + escaped.replace(/\*/g, '[^/]+') + '(\\?.*)?$');
        return regex.test(path) || regex.test(url);
      }

      // 2. Handle exact matches or path-start matches
      const patternOnly = pattern.split('?')[0];

      return path === patternOnly ||
        url === pattern ||
        url.startsWith(pattern + '?') ||
        url.startsWith(pattern + '/');
    })) {
      return { ...config, category };
    }
  }

  return {
    category: API_CATEGORIES.SEMI_DYNAMIC,
    ...CACHE_CONFIG[API_CATEGORIES.SEMI_DYNAMIC],
  };
};

/**
 * Helper to generate cache key from URL and params
 */
export const getCacheKey = (url, params = {}) => {
  const path = normalizeUrl(url);

  // If params is already a string, use it. Otherwise, stringify the object.
  let paramString = '';
  if (typeof params === 'string') {
    paramString = params;
  } else if (params && typeof params === 'object') {
    paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
  }

  return `${path}${paramString ? '?' + paramString : ''}`;
};

/**
 * Check if endpoint should be cached
 */
export const shouldCache = (endpoint, method = 'GET') => {
  // Only cache GET requests
  if (method.toUpperCase() !== 'GET') {
    return false;
  }

  const config = getCacheConfig(endpoint);
  return config.cacheDuration > 0;
};
